import { IQuoteBasicRespSchema } from '@monetize/types/app';
import { pluralize, toDateShort } from '@monetize/utils/core';
import { memo } from 'react';
import { Link } from 'react-router-dom';
import { CONTRACT_STATUS_ACCENT_COLOR } from '../../../constants/contracts';
import { QuoteStatusEnumDisplay } from '../../../constants/quotes';
import { getQuoteLink } from '../../../utils/quotes';
import {
  MBox,
  MGrid,
  MHStack,
  MIDCopyBox,
  MText,
  MTooltip,
} from '../../Monetize';
import { MCurrency } from '../../Monetize/MCurrency';

// Pending terms are usually renewals, so a renewal in progress shares their accent
export const RENEWAL_ACCENT_COLOR = CONTRACT_STATUS_ACCENT_COLOR.PENDING;

/** Tooltip body: the quote's name, then term length, created date and id */
export const RenewalQuoteDetails = ({
  quote: { id, description, contractLength, createDate },
}: {
  quote: IQuoteBasicRespSchema;
}) => (
  <MBox fontSize="xs">
    <MText fontSize="inherit" fontWeight="semibold" color="inherit" mb={1}>
      {description}
    </MText>
    <MGrid templateColumns="auto 1fr" columnGap={3} rowGap={0.5}>
      <MText fontSize="inherit" color="inherit" fontWeight="semibold">
        Term
      </MText>
      <MText fontSize="inherit" color="inherit">
        {contractLength} {pluralize('month', contractLength)}
      </MText>
      <MText fontSize="inherit" color="inherit" fontWeight="semibold">
        Created
      </MText>
      <MText fontSize="inherit" color="inherit">
        {toDateShort(createDate)}
      </MText>
      <MText fontSize="inherit" color="inherit" fontWeight="semibold">
        Quote ID
      </MText>
      <MText fontSize="inherit" color="inherit">
        {id}
      </MText>
    </MGrid>
  </MBox>
);

/**
 * A renewal quote that has not become a contract yet. Sits after the
 * lineage's terms, dashed like a pending term, and opens the quote. Its ID
 * shows beneath, click to copy, like a term's contract ID.
 */
export const RenewalQuoteChip = memo(
  ({ quote }: { quote: IQuoteBasicRespSchema }) => {
    const {
      id,
      status,
      description,
      contractStartDate,
      contractEndDate,
      amount,
      currency,
    } = quote;
    const statusDisplay = QuoteStatusEnumDisplay[status];
    const startDate = toDateShort(contractStartDate);
    const endDate = toDateShort(contractEndDate);

    // The ID copies on click, so it sits beside the quote's link rather than
    // inside it; the container's hover and focus follow the link alone
    return (
      <MBox
        position="relative"
        flexShrink={0}
        minW="210px"
        borderRadius="md"
        borderWidth="1px"
        borderStyle="dashed"
        borderColor="border.default"
        bg="bg.surface"
        transition="box-shadow 0.12s ease, border-color 0.12s ease"
        _before={{
          content: '""',
          position: 'absolute',
          left: '-1px',
          top: '-1px',
          bottom: '-1px',
          borderLeftWidth: '4px',
          borderLeftStyle: 'solid',
          borderLeftColor: RENEWAL_ACCENT_COLOR,
          borderLeftRadius: 'md',
        }}
        css={{
          '&:has(> a:hover)': { borderColor: 'border.accent' },
          '&:has(> a:focus-visible)': { boxShadow: 'focus' },
        }}
      >
        <MTooltip label={<RenewalQuoteDetails quote={quote} />} placement="top">
          <MBox
            asChild
            data-testid={`contract-renewal-quote-chip-${id}`}
            aria-label={`Renewal quote ${description}, ${statusDisplay}, ${startDate} to ${endDate}`}
            display="block"
            px={3}
            pt={2}
            pl={4}
            textDecoration="none"
            _hover={{ textDecoration: 'none' }}
            _focusVisible={{ outline: 'none' }}
          >
            <Link to={getQuoteLink({ quoteId: id, status })}>
              <MText
                fontSize="xxs"
                fontWeight="bold"
                letterSpacing="0.05em"
                textTransform="uppercase"
                color="fg.muted"
                lineHeight="20px"
              >
                Renewal quote
              </MText>
              <MText
                fontWeight="semibold"
                color="fg.strong"
                whiteSpace="nowrap"
              >
                {startDate} – {endDate}
              </MText>
              <MHStack gap={2} fontSize="xs" color="fg.muted" mt={0.5}>
                <MText
                  as="span"
                  fontSize="inherit"
                  fontWeight="semibold"
                  color={RENEWAL_ACCENT_COLOR}
                >
                  {statusDisplay}
                </MText>
                {amount != null && (
                  <MCurrency
                    as="span"
                    amount={amount}
                    options={{ currency }}
                    fontSize="inherit"
                    fontWeight="semibold"
                    color="fg.primary"
                  />
                )}
              </MHStack>
            </Link>
          </MBox>
        </MTooltip>
        <MBox px={3} pl={4} pt={0.5} pb={2}>
          <MIDCopyBox copyValue={id} displayIcon={false} fontSize="xs" />
        </MBox>
      </MBox>
    );
  },
);
